import Button from 'components/_UI/Button';
import Ilustration from 'components/_UI/Ilustration';
import Text from 'components/_UI/Text';
import React, { useEffect, useState } from 'react'
import { PlusSquare, Users } from 'lucide-react';
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import teamsApi from 'api/requests/teams';
import { FullyCentered } from 'styled/alignment/Center';
import Loader from 'components/_UI/Loader';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import DashCollapse from 'components/_UI/DashCollapse';
import TeamFormModal from './modals/TeamFormModal';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import TeamDetailPanel from './TeamDetailPanel';
import TeamCard from './TeamCard';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';

export default function TeamsPage() {
    const { can } = usePanelPermissions();
    const canManage = can(PERMISSIONS.TEAMS_MANAGE);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formModalVisible, setFormModalVisible] = useState(false);
    const [editingTeam, setEditingTeam] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' });
    const [deleting, setDeleting] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const { toast, toasts } = useToast();

    useEffect(() => {
        fetchTeams();
    }, []);

    async function fetchTeams() {
        setLoading(true);

        try {
            const { data } = await teamsApi.get();
            setTeams(data?.data || []);
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    function onFormSuccess() {
        setFormModalVisible(false);
        setEditingTeam(false);
        fetchTeams();
        toast.success('Equipe salva com sucesso!', { position: 'top-center' })
    }

    function onEditClick(team) {
        setEditingTeam(team);
        setFormModalVisible(true);
    }

    function onDeleteClick(team) {
        setConfirmDelete({ open: true, id: team.id, name: team.name });
    }

    async function handleDelete() {
        setDeleting(true);

        try {
            await teamsApi.delete(confirmDelete.id);

            if (selectedTeamId === confirmDelete.id) {
                setSelectedTeamId(null);
            }

            fetchTeams();
            toast.success('Equipe removida com sucesso!', { position: 'top-center' })
        } catch (error) {

        } finally {
            setDeleting(false);
            setConfirmDelete({ open: false, id: null, name: '' });
        }
    }

    const selectedTeam = teams.find(team => team.id === selectedTeamId);

    return (
        <Page>
            <ToastContainer toasts={toasts} />

            <HeaderRow>
                <SectionHeader>
                    <SectionIcon>
                        <Users size={16} color={panelAccent(colors.secondaryPurple)} />
                    </SectionIcon>
                    <HeaderContent>
                        <SectionTitle>Equipes</SectionTitle>
                        <SectionSubtitle>
                            {loading ? 'Carregando...' : `${teams.length} ${teams.length === 1 ? 'equipe' : 'equipes'}`}
                        </SectionSubtitle>
                    </HeaderContent>
                </SectionHeader>

                {canManage && (
                    <Button
                        variant='featured'
                        category='squired'
                        onClick={() => setFormModalVisible(true)}
                    >
                        <PlusSquare color={colors.neutralLight} size={16} />
                        <Text
                            color={colors.neutralLight}
                            paddingBottom={0}
                            marginLeft={'6px'}
                        >
                            Nova equipe
                        </Text>
                    </Button>
                )}
            </HeaderRow>

            <Text marginTop={'-10px'} marginBottom={'6px'}>
                Monte equipes de colaboradores e conceda acesso ao painel de uma vez só
            </Text>

            {
                formModalVisible && (
                    <TeamFormModal
                        visible={formModalVisible}
                        edit={editingTeam}
                        onClose={() => { setFormModalVisible(false); setEditingTeam(false); }}
                        onSuccess={onFormSuccess}
                    />
                )
            }

            <ConfirmDialog
                isOpen={confirmDelete.open}
                title='Remover equipe'
                message={`Tem certeza que deseja remover a equipe "${confirmDelete.name}"? Os membros perdem o acesso concedido a ela imediatamente.`}
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete({ open: false, id: null, name: '' })}
            />

            {
                loading ? (
                    <ContentLoader>
                        <Loader />
                    </ContentLoader>
                ) : (
                    teams.length ? (
                        <CardsGrid>
                            {teams.map(team => (
                                <TeamCard
                                    key={team.id}
                                    team={team}
                                    onSelect={(t) => setSelectedTeamId(t.id)}
                                    onEdit={onEditClick}
                                    onDelete={onDeleteClick}
                                />
                            ))}
                        </CardsGrid>
                    ) : (
                        <Column fullyCentralized height='auto'>
                            <Picture>
                                <Ilustration name='collaboration' />
                            </Picture>

                            <Text type='bold' color={colors.night} textAlign={'center'} name='normal' marginTop={'-35px'}>
                                Você ainda não criou nenhuma equipe.
                            </Text>

                            {canManage && <FullyCentered marginTop='10px'>
                                <Column width='250px'>
                                    <Button
                                        variant='featured'
                                        category='squired'
                                        onClick={() => setFormModalVisible(true)}
                                    >
                                        <PlusSquare color={colors.neutralLight} size={16} />
                                        <Text
                                            color={colors.neutralLight}
                                            paddingBottom={0}
                                            marginLeft={'6px'}
                                        >
                                            Nova equipe
                                        </Text>
                                    </Button>
                                </Column>
                            </FullyCentered>}
                        </Column>
                    )
                )
            }

            <DashCollapse
                visible={!!selectedTeamId}
                onClose={() => setSelectedTeamId(null)}
                edit={!!selectedTeam}
                clickedAction={({ action }) => { if (action === 'edit' && selectedTeam) onEditClick(selectedTeam); }}
            >
                {
                    selectedTeam && (
                        <TeamDetailPanel
                            team={selectedTeam}
                            onChange={fetchTeams}
                        />
                    )
                }
            </DashCollapse>
        </Page>
    )
}

const Page = styled(Column)`
    width: 100%;
`;

const HeaderRow = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 6px;

    @media screen and (min-width: 900px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        & > button {
            width: auto;
        }
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const SectionSubtitle = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    font-weight: 500;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
    margin-top: 14px;
`;

const Picture = styled.picture`
    width: auto;
    height: 300px;
`;

const ContentLoader = styled(Column)`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;
